import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import twilioClient from '../../utils/twilioClient';
import IVRConsole from './IVRConsole';

const { FiRefreshCw, FiEdit2, FiTrash2, FiGlobe, FiPhoneIncoming, FiMessageSquare, FiLayers } = FiIcons;

const MyNumbersList = ({ onRefresh }) => {
  const [numbers, setNumbers] = useState([]);
  const [ivrs, setIvrs] = useState([]);
  const [ivrMenus, setIvrMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingNumber, setEditingNumber] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load numbers with root-anchored URL
      const res = await fetch("/api/twilio/numbers");
      const data = await res.json();
      if (!data || data.ok === false) {
        setError(
          data?.error || "Failed to load Twilio numbers. Please check the backend logs."
        );
        setNumbers([]);
        return;
      }
      const list = Array.isArray(data.numbers) ? data.numbers : [];

      // Load IVRs
      let ivrsData = [];
      try {
        const ivrsRes = await fetch("/api/twilio/ivrs");
        const ivrsDataRes = await ivrsRes.json();
        ivrsData = ivrsDataRes || [];
      } catch (e) {
        console.warn('Failed to load IVRs:', e);
        ivrsData = [];
      }

      // Load IVR menus from NCB via new backend proxy
      let menusData = [];
      try {
        const menusRes = await fetch('/api/ncb/get-ivr-menus');
        
        if (!menusRes.ok) {
          console.warn("[IVR] Failed to load IVR menus (status " + menusRes.status + ")");
          menusData = [];
        } else {
          const parsedData = await menusRes.json();
          if (parsedData) {
            const rows = Array.isArray(parsedData) ? parsedData : (parsedData.menus || parsedData.data || []);
            // Map rows and handle JSON parsing for options
            menusData = rows.map(row => {
              let options = [];
              try {
                options = typeof row.options === 'string' ? JSON.parse(row.options) : (row.options || []);
              } catch (_) {
                options = [];
              }
              return {
                id: row.id || row.menu_id,
                prompt: row.title || row.prompt || "Untitled Menu",
                options
              };
            });
          }
        }
      } catch (e) {
        console.warn('Failed to load IVR menus:', e);
        menusData = [];
      }

      setNumbers(list);
      setIvrs(ivrsData);
      setIvrMenus(menusData);
    } catch (err) {
      setError(
        err?.message || "Unexpected error loading Twilio numbers."
      );
      setNumbers([]);
      setIvrs([]);
      setIvrMenus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRelease = async (sid) => {
    if (!window.confirm('Are you sure you want to release this number? This cannot be undone.')) return;
    const confirmText = prompt('Type RELEASE to confirm:');
    if (confirmText !== 'RELEASE') return;

    try {
      const res = await fetch(`/api/twilio/release-number`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sid })
      });
      const data = await res.json();
      if (!data.ok) {
        throw new Error(data.error || 'Failed to release number');
      }
      alert('Number released.');
      // Refresh numbers
      loadData();
      // Notify parent component
      if (onRefresh) {
        onRefresh();
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingNumber) return;

    try {
      const res = await fetch(`/api/twilio/update-number`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sid: editingNumber.sid,
          friendlyName: editingNumber.friendlyName,
          smsWebhookType: editingNumber.smsWebhookType,
          smsWebhookUrl: editingNumber.smsWebhookType === 'custom' ? editingNumber.smsWebhookUrl : '',
          voiceWebhookType: editingNumber.voiceWebhookType,
          voiceWebhookUrl: editingNumber.voiceWebhookType === 'custom' ? editingNumber.voiceWebhookUrl : '',
          ivrId: editingNumber.ivrId,
          ivrMenu: editingNumber.ivrMenu
        })
      });
      const data = await res.json();
      if (!data.ok) {
        throw new Error(data.error || 'Failed to update number');
      }
      setEditingNumber(null);
      // Reload numbers
      loadData();
      // Notify parent component
      if (onRefresh) {
        onRefresh();
      }
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading Twilio numbers...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500 text-sm">{error}</div>;
  }

  if (!numbers.length) {
    return <div className="text-center py-8 text-gray-500">No Twilio numbers found for this account.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={loadData}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <SafeIcon icon={FiRefreshCw} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="space-y-4">
        {numbers.map(num => (
          <div key={num.sid} className="bg-white p-4 rounded-lg shadow border flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <div className="flex items-center">
                <h3 className="text-lg font-bold text-gray-900 mr-2">{num.phoneNumber}</h3>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{num.friendlyName}</span>
              </div>
              <div className="mt-1 text-sm text-gray-600 flex flex-col space-y-1">
                <div className="flex items-center"><SafeIcon icon={FiGlobe} className="mr-2 text-gray-400" /> {num.isoCountry}</div>
                <div className="flex items-center"><SafeIcon icon={FiMessageSquare} className="mr-2 text-gray-400" /> SMS: <span className="ml-1 truncate max-w-xs">{num.smsUrl || 'None'}</span></div>
                <div className="flex items-center"><SafeIcon icon={FiPhoneIncoming} className="mr-2 text-gray-400" /> Voice: <span className="ml-1 truncate max-w-xs">{num.voiceUrl || 'None'}</span></div>
                {num.voiceUrl?.includes('ivr/handle') && (
                  <div className="flex items-center"><SafeIcon icon={FiLayers} className="mr-2 text-blue-400" /> IVR: <span className="ml-1 text-blue-600">{num.voiceUrl?.split('CurrentMenu=')[1] || 'Attached'}</span></div>
                )}
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex space-x-2">
              <button
                onClick={() => setEditingNumber({
                  ...num,
                  smsWebhookType: num.smsUrl?.includes('incoming-sms') ? 'default' : num.smsUrl ? 'custom' : 'none',
                  smsWebhookUrl: num.smsUrl || '',
                  voiceWebhookType: num.voiceUrl?.includes('ivr/handle') ? 'ivr' : num.voiceUrl?.includes('default') ? 'default' : num.voiceUrl ? 'custom' : 'none',
                  voiceWebhookUrl: num.voiceUrl || '',
                  ivrId: num.voiceUrl?.includes('ivrId=') ? num.voiceUrl.split('ivrId=')[1].split('&')[0] : '',
                  ivrMenu: num.voiceUrl?.includes('CurrentMenu=') ? num.voiceUrl.split('CurrentMenu=')[1] : ''
                })}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
              >
                <SafeIcon icon={FiEdit2} />
              </button>
              <button onClick={() => handleRelease(num.sid)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                <SafeIcon icon={FiTrash2} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editingNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Number Settings</h2>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Friendly Name</label>
                <input
                  type="text"
                  value={editingNumber.friendlyName || ''}
                  onChange={e => setEditingNumber({ ...editingNumber, friendlyName: e.target.value })}
                  className="w-full border rounded p-2"
                />
              </div>

              {/* SMS Config */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">SMS Handler</label>
                <select
                  value={editingNumber.smsWebhookType}
                  onChange={e => setEditingNumber({ ...editingNumber, smsWebhookType: e.target.value })}
                  className="w-full border rounded p-2 mb-2"
                >
                  <option value="none">None</option>
                  <option value="default">Default Inbound Log</option>
                  <option value="custom">Custom URL</option>
                </select>
                {editingNumber.smsWebhookType === 'custom' && (
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editingNumber.smsWebhookUrl}
                    onChange={e => setEditingNumber({ ...editingNumber, smsWebhookUrl: e.target.value })}
                    className="w-full border rounded p-2"
                  />
                )}
              </div>

              {/* Voice Config */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">Voice Handler</label>
                <select
                  value={editingNumber.voiceWebhookType}
                  onChange={e => setEditingNumber({ ...editingNumber, voiceWebhookType: e.target.value })}
                  className="w-full border rounded p-2 mb-2"
                >
                  <option value="none">None</option>
                  <option value="default">Default "Say & Hangup"</option>
                  <option value="ivr">IVR Menu</option>
                  <option value="custom">Custom URL</option>
                </select>

                {editingNumber.voiceWebhookType === 'ivr' && (
                  <select
                    value={editingNumber.ivrMenu}
                    onChange={e => setEditingNumber({ ...editingNumber, ivrMenu: e.target.value })}
                    className="w-full border rounded p-2"
                  >
                    <option value="">Select an IVR Menu...</option>
                    {ivrMenus.map(menu => (
                      <option key={menu.id} value={menu.id}>
                        {menu.prompt ? menu.prompt.substring(0, 30) : `Menu ${menu.id}`}...
                      </option>
                    ))}
                  </select>
                )}

                {editingNumber.voiceWebhookType === 'custom' && (
                  <input
                    type="url"
                    placeholder="https://..."
                    value={editingNumber.voiceWebhookUrl}
                    onChange={e => setEditingNumber({ ...editingNumber, voiceWebhookUrl: e.target.value })}
                    className="w-full border rounded p-2"
                  />
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingNumber(null)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyNumbersList;